import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassIcon,
  Assignment as TaskIcon,
  Flag as FlagIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import {
  getSubmissionStatus,
  sendCitizenMessage,
  completeCitizenTask,
  uploadCitizenDocument,
  transformBackendForm,
} from '../api/citizenApi';
import type {
  CitizenStatusDto,
  CitizenTaskDto,
  PublicDocumentDto,
  PublicCommentDto,
  TimelineStepDto,
  DecisionDto,
} from '../api/types';
import { DynamicForm } from '../components/forms';

const statusConfig: Record<string, { label: string; color: 'warning' | 'info' | 'success' | 'secondary' }> = {
  PENDING: { label: 'Eingereicht', color: 'warning' },
  IN_REVIEW: { label: 'In Bearbeitung', color: 'info' },
  COMPLETED: { label: 'Abgeschlossen', color: 'success' },
  WAITING_FOR_CITIZEN: { label: 'Ihre Mitwirkung erforderlich', color: 'secondary' },
};

const decisionLabels: Record<string, { label: string; icon: typeof CheckCircleIcon; color: string }> = {
  approved: { label: 'Genehmigt', icon: CheckCircleIcon, color: 'success.main' },
  rejected: { label: 'Abgelehnt', icon: CancelIcon, color: 'error.main' },
};

// --- Feed Event Types ---

type FeedEvent =
  | { type: 'process_started'; timestamp: string }
  | { type: 'step_completed'; step: TimelineStepDto; timestamp: string }
  | { type: 'step_active'; step: TimelineStepDto; timestamp: string }
  | { type: 'comment'; comment: PublicCommentDto; timestamp: string }
  | { type: 'document'; document: PublicDocumentDto; timestamp: string }
  | { type: 'decision'; decision: DecisionDto; timestamp: string }
  | { type: 'citizen_task'; task: CitizenTaskDto };

function buildFeedEvents(status: CitizenStatusDto): FeedEvent[] {
  const events: FeedEvent[] = [];

  // Process started
  events.push({ type: 'process_started', timestamp: status.submittedAt });

  // Timeline steps
  for (const step of status.timeline) {
    if (step.status === 'COMPLETED' && step.completedAt) {
      events.push({ type: 'step_completed', step, timestamp: step.completedAt });
    } else if (step.status === 'ACTIVE' && step.startedAt) {
      events.push({ type: 'step_active', step, timestamp: step.startedAt });
    }
  }

  // Comments
  for (const comment of status.comments) {
    events.push({ type: 'comment', comment, timestamp: comment.createdAt });
  }

  // Documents
  for (const doc of status.documents) {
    events.push({ type: 'document', document: doc, timestamp: doc.uploadedAt });
  }

  // Decision
  if (status.decision) {
    events.push({
      type: 'decision',
      decision: status.decision,
      timestamp: status.decision.decidedAt || status.submittedAt,
    });
  }

  // Sort chronologically
  events.sort((a, b) => {
    const tA = 'timestamp' in a ? new Date(a.timestamp).getTime() : Infinity;
    const tB = 'timestamp' in b ? new Date(b.timestamp).getTime() : Infinity;
    return tA - tB;
  });

  // Citizen tasks always at the end
  for (const task of status.citizenTasks) {
    events.push({ type: 'citizen_task', task });
  }

  return events;
}

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// --- Sub-Components ---

function SystemMessage({ icon, text, timestamp }: { icon: React.ReactNode; text: string; timestamp: string }) {
  return (
    <Box
      data-testid="timeline-step"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1 }}
    >
      {icon}
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        {formatTimestamp(timestamp)}
      </Typography>
    </Box>
  );
}

function CommentBubble({ comment }: { comment: PublicCommentDto }) {
  const isCitizen = comment.author === 'Bürger';
  return (
    <Paper
      sx={{
        p: 2,
        bgcolor: isCitizen ? 'primary.main' : 'action.hover',
        color: isCitizen ? 'primary.contrastText' : 'text.primary',
        ml: isCitizen ? 6 : 0,
        mr: isCitizen ? 0 : 6,
        borderRadius: 2,
      }}
      variant="outlined"
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2" color="inherit">
          {comment.author}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }} color="inherit">
          {formatTimestamp(comment.createdAt)}
        </Typography>
      </Box>
      <Typography variant="body2" color="inherit">
        {comment.content}
      </Typography>
    </Paper>
  );
}

function DocumentCard({ document }: { document: PublicDocumentDto }) {
  const isCitizen = document.uploadedBy === 'Bürger';
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        ml: isCitizen ? 6 : 0,
        mr: isCitizen ? 0 : 6,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <AttachFileIcon fontSize="small" color="action" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap fontWeight="medium">
          {document.fileName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatFileSize(document.fileSize)}
          {document.description && ` — ${document.description}`}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.disabled">
        {formatTimestamp(document.uploadedAt)}
      </Typography>
    </Paper>
  );
}

function DecisionCard({ decision }: { decision: DecisionDto }) {
  const config = decisionLabels[decision.outcome];
  const Icon = config?.icon ?? CheckCircleIcon;
  const label = config?.label ?? decision.outcome;
  const color = config?.color ?? 'text.primary';

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderColor: color, borderWidth: 2 }}
      data-testid="decision-display"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: decision.reason ? 1 : 0 }}>
        <Icon sx={{ color }} />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color }}>
          Entscheidung: {label}
        </Typography>
        {decision.decidedAt && (
          <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
            {formatTimestamp(decision.decidedAt)}
          </Typography>
        )}
      </Box>
      {decision.reason && (
        <Typography variant="body2" color="text.secondary">
          {decision.reason}
        </Typography>
      )}
    </Paper>
  );
}

function CitizenTaskForm({
  task,
  onComplete,
  isSubmitting,
}: {
  task: CitizenTaskDto;
  onComplete: (data: Record<string, unknown>) => void;
  isSubmitting: boolean;
}) {
  const formDef = useMemo(() => {
    if (!task.formDefinition) return null;
    return transformBackendForm(task.formDefinition);
  }, [task.formDefinition]);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 3, borderColor: 'secondary.main', borderWidth: 2 }}
      data-testid={`citizen-task-${task.taskId}`}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TaskIcon color="secondary" />
        <Typography variant="subtitle1" fontWeight="bold">
          {task.name}
        </Typography>
      </Box>

      {task.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {task.description}
        </Typography>
      )}

      {formDef ? (
        <DynamicForm
          formDefinition={formDef}
          onSubmit={onComplete}
          isProcessing={isSubmitting}
          submitButtonText="Absenden"
          processingText="Wird gesendet..."
        />
      ) : (
        <Button
          variant="contained"
          onClick={() => onComplete({})}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
        >
          {isSubmitting ? 'Wird gesendet...' : 'Bestätigen'}
        </Button>
      )}
    </Paper>
  );
}

function FeedEventItem({
  event,
  onCompleteTask,
  completingTask,
}: {
  event: FeedEvent;
  onCompleteTask: (task: CitizenTaskDto, data: Record<string, unknown>) => void;
  completingTask: string | null;
}) {
  switch (event.type) {
    case 'process_started':
      return (
        <SystemMessage
          icon={<FlagIcon fontSize="small" color="warning" />}
          text="Antrag eingereicht"
          timestamp={event.timestamp}
        />
      );
    case 'step_completed':
      return (
        <SystemMessage
          icon={<CheckCircleIcon fontSize="small" color="success" />}
          text={`Schritt abgeschlossen: ${event.step.name}`}
          timestamp={event.timestamp}
        />
      );
    case 'step_active':
      return (
        <SystemMessage
          icon={<HourglassIcon fontSize="small" color="info" />}
          text={`Aktueller Schritt: ${event.step.name}`}
          timestamp={event.timestamp}
        />
      );
    case 'comment':
      return <CommentBubble comment={event.comment} />;
    case 'document':
      return <DocumentCard document={event.document} />;
    case 'decision':
      return <DecisionCard decision={event.decision} />;
    case 'citizen_task':
      return (
        <CitizenTaskForm
          task={event.task}
          onComplete={(data) => onCompleteTask(event.task, data)}
          isSubmitting={completingTask === event.task.taskId}
        />
      );
  }
}

// --- Main Page ---

export function StatusCheckPage() {
  const [searchParams] = useSearchParams();
  const [referenceNumber, setReferenceNumber] = useState(searchParams.get('ref') || '');
  const [status, setStatus] = useState<CitizenStatusDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [completingTask, setCompletingTask] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!referenceNumber.trim()) return;

    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const result = await getSubmissionStatus(referenceNumber.trim());
      setStatus(result);
    } catch {
      setError('Antrag nicht gefunden. Bitte prüfen Sie die Referenznummer.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !status) return;

    setSending(true);
    try {
      await sendCitizenMessage(status.confirmationNumber, message.trim());
      setMessage('');
      const result = await getSubmissionStatus(status.confirmationNumber);
      setStatus(result);
    } catch {
      setError('Nachricht konnte nicht gesendet werden.');
    } finally {
      setSending(false);
    }
  };

  const handleCompleteTask = async (task: CitizenTaskDto, data: Record<string, unknown>) => {
    if (!status) return;

    setCompletingTask(task.taskId);
    try {
      const variables: Record<string, unknown> = { ...data };
      for (const [key, value] of Object.entries(variables)) {
        if (value instanceof File) {
          const doc = await uploadCitizenDocument(
            status.confirmationNumber,
            value,
            task.taskId
          );
          variables[key] = doc.fileName;
        }
      }

      await completeCitizenTask(status.confirmationNumber, task.taskId, variables);
      const result = await getSubmissionStatus(status.confirmationNumber);
      setStatus(result);
    } catch {
      setError('Aufgabe konnte nicht abgeschlossen werden.');
    } finally {
      setCompletingTask(null);
    }
  };

  useEffect(() => {
    if (searchParams.get('ref')) {
      handleSearch();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formattedDate = status
    ? formatTimestamp(status.submittedAt)
    : '';

  const cfg = status ? statusConfig[status.status] ?? statusConfig.PENDING : null;

  const feedEvents = useMemo(() => {
    if (!status) return [];
    return buildFeedEvents(status);
  }, [status]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 700, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom textAlign="center">
            Antragsstatus prüfen
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
            Geben Sie Ihre Referenznummer ein, um den Status Ihres Antrags abzurufen.
          </Typography>

          {/* Search */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <TextField
              fullWidth
              label="Referenznummer"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="ANT-..."
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !referenceNumber.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              Status abrufen
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {status && cfg && (
            <>
              <Divider sx={{ mb: 3 }} />

              {/* Status Badge */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Chip
                  data-testid="status-badge"
                  label={cfg.label}
                  color={cfg.color}
                  size="medium"
                  sx={{ fontSize: '1rem', px: 2, py: 2.5 }}
                />
              </Box>

              {/* Details */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Referenznummer:</strong> {status.confirmationNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Eingereicht am:</strong> {formattedDate}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Behörde:</strong> {status.authorityName}
                </Typography>
                {status.currentActivityName && status.status !== 'COMPLETED' && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Aktueller Schritt:</strong> {status.currentActivityName}
                  </Typography>
                )}
              </Paper>

              {/* Unified Feed */}
              {(() => {
                const timelineEvents = feedEvents.filter((e) => e.type !== 'citizen_task');
                const taskEvents = feedEvents.filter((e) => e.type === 'citizen_task');
                return (
                  <Box
                    data-testid="event-feed"
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}
                  >
                    {timelineEvents.map((event, index) => (
                      <FeedEventItem
                        key={index}
                        event={event}
                        onCompleteTask={handleCompleteTask}
                        completingTask={completingTask}
                      />
                    ))}
                    {taskEvents.length > 0 && (
                      <Box data-testid="citizen-tasks" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {taskEvents.map((event, index) => (
                          <FeedEventItem
                            key={`task-${index}`}
                            event={event}
                            onCompleteTask={handleCompleteTask}
                            completingTask={completingTask}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Send Message (only if not completed) */}
              {status.status !== 'COMPLETED' && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Nachricht an die Behörde
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Ihre Nachricht..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      multiline
                      maxRows={3}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSendMessage}
                      disabled={sending || !message.trim()}
                    >
                      {sending ? <CircularProgress size={20} /> : <SendIcon />}
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
